import _ from "lodash";
import { useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ArrowBreadcrumb } from "../../components/icons";
import { LoadingOverlay } from "../../components/LoadingOverlay/LoadingOverlay";
import { PageTitle } from "../../components/PageTitle";
import { useLocationContext } from "../../contexts/locationContext";
import { useOrganizationContext } from "../../contexts/organizations/organizationsContext";
import type { IApplication } from "../../hooks/reactQueryHooks/useApplications";
import { useApplications } from "../../hooks/reactQueryHooks/useApplications";
import type { ICacheItemResponsePayload } from "../../hooks/reactQueryHooks/useCacheItems";
import {
  getHistory,
  getPrompt,
  useCacheItem,
} from "../../hooks/reactQueryHooks/useCacheItems";
import type { Message } from "../../hooks/reactQueryHooks/useRequests";
import { CacheItemActions } from "./components/CacheItemActions/CacheItemActions";
import { ManageCacheItemPage } from "./components/ManageCacheItemPage/ManageCacheItemPage";

const settingsBreadcrumbs = [
  {
    title: "Safety & Compliance",
    path: "",
    isSeparator: false,
    isActive: false,
  },
  {
    title: "",
    path: "",
    isSeparator: true,
    isActive: false,
  },
  {
    title: "Cache",
    path: "",
    isSeparator: false,
    isActive: false,
    to: "/safety-compliance/cache",
  },
  {
    title: "",
    path: "",
    isSeparator: true,
    isActive: false,
  },
];

const ManageCacheItemWrapper = () => {
  const { id, projId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [cacheData, setCacheData] = useState<ICacheItemResponsePayload | null>(
    null,
  );
  const [app, setApp] = useState<IApplication | null>(null);
  const [pageTitle, setPageTitle] = useState<string>("");
  const [cacheHistory, setCacheHistory] = useState<Message[]>([]);

  const { currentOrganization } = useOrganizationContext();
  const { prevLocation, setPrevLocation } = useLocationContext();
  const { data: appData } = useApplications(currentOrganization?.id || "");

  const { isLoading, isError, isSuccess, isFetching, data } = useCacheItem(
    id || "",
    currentOrganization?.id || "",
    projId || "",
  );

  useEffect(() => {
    if (data && isSuccess) {
      const history = getHistory(data);
      const prompt = getPrompt(data);
      const choices = data.responsePayload.choices;
      if (!pageTitle) {
        setPageTitle(prompt);
      }
      setCacheData({ prompt, choices });
      setCacheHistory(history);
    }
  }, [data, isSuccess, pageTitle]);

  const refetchCacheItem = () => {
    queryClient.removeQueries("cacheItem");
    setCacheData(null);
    setCacheHistory([]);
  };

  useEffect(() => {
    if (appData) {
      const app = appData.find(app => app.id === data?.applicationId);
      setApp(app || null);
    }
  }, [appData, data, data?.applicationId]);

  useEffect(() => {
    if (isError) {
      navigate("/error/404");
    }
  }, [isError, navigate]);

  return (
    <>
      <div className="d-flex align-items-center">
        {(isLoading || isFetching) && <LoadingOverlay />}
        <Link
          to={prevLocation ? prevLocation : "/safety-compliance/cache"}
          className="me-3"
          onClick={() => {
            setPrevLocation(null);
          }}
        >
          <ArrowBreadcrumb className="svg-icon" />
        </Link>
        <PageTitle
          breadcrumbs={settingsBreadcrumbs}
          breadcrumbTitle="Manage cache item"
          pageTitle={pageTitle}
        />

        <div className="ms-auto" />
        <CacheItemActions
          applicationId={app?.id || ""}
          isDisabledDeleteBtn={data?.state !== "Ready"}
        />
      </div>
      <ManageCacheItemPage
        cacheData={cacheData}
        appName={app?.name || ""}
        projId={app?.id || ""}
        similarPrompts={data?.similarPrompts || []}
        cacheId={id || ""}
        currentOrganization={currentOrganization}
        fetchItem={refetchCacheItem}
        promptContext={data?.promptContext}
        promptType={data?.promptType}
        cacheHistory={cacheHistory}
      />
    </>
  );
};

export { ManageCacheItemWrapper };
